/**
 * Motor das chamadas de vídeo/voz do chat da equipe (2026-10-09).
 *
 * [Bruno] "chamada de vídeo igual o Teams, feita direto pelo chat, sem abrir
 * nada externo". Tudo no navegador: câmera e microfone pelo `getUserMedia`, a
 * mídia vai DIRETO entre os computadores (WebRTC) e o servidor só repassa os
 * sinais de combinação pelo mesmo tempo real do chat.
 *
 * Quem ENTRA na chamada liga pra quem já está (manda a oferta); quem já está só
 * responde. Assim nunca duas pessoas oferecem ao mesmo tempo uma pra outra.
 *
 * Cada conexão nasce com TRÊS canais fixos, nesta ordem: áudio, câmera e tela
 * (mesmo desenho do HUB AMT). Ligar a câmera depois, trocar de microfone ou
 * compartilhar a tela é só trocar a faixa do canal, sem recombinar; e a tela
 * não substitui a câmera: quem assiste vê as duas.
 *
 * Estado único, global (a chamada continua se a pessoa sair da tela do chat):
 * quem desenha assina com `assinar(fn)`.
 */
import api, { API_BASE } from '../services/api';
import { ouvir, usar } from './chatEquipeStream';

const ICE_PADRAO = [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }];
const ORDEM = ['audio', 'video', 'tela']; // canais fixos de cada conexão
export const REACOES = ['👍', '👏', '❤️', '😂', '🎉', '😮'];
const PREFS = 'cockpit.chamadaPrefs';

const vivo = (t) => !!(t && t.readyState === 'live');

const st = {
  fase: 'livre', // livre | tocando (recebendo) | chamando (aguardando atender) | em_chamada | encerrada
  canalId: null,
  canalNome: '',
  video: true,
  convite: null, // { de:{id,nome}, canal_nome, canal_tipo, video }
  inicio: null,
  aviso: '',
  local: { mic: false, cam: false, tela: false, mao: false, falando: false, camStream: null, telaStream: null, telaAba: false },
  pares: new Map(), // uid → { nome, pc, remoto, telaStream, mic, cam, tela, mao, conexao, falando, filaIce, trans }
  ativas: new Map(), // canalId → resumo da chamada em andamento (pra mostrar "Entrar")
  brindes: [], // avisos curtos que somem: "Fulano entrou", "levantou a mão"…
  reacoes: [], // emojis subindo: { id, quem: 'eu' | uid, emoji }
  prefs: lerPrefs(),
  ultimoFim: null,
};
let iceServers = ICE_PADRAO;
let micTrack = null;
let camTrack = null;
let telaTrack = null;
let seq = 0;
const assinantes = new Set();

function lerPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS) || '{}') || {}; } catch (_) { return {}; }
}
function salvarPrefs() {
  try { localStorage.setItem(PREFS, JSON.stringify(st.prefs)); } catch (_) { /* navegador sem armazenamento */ }
}
function meuId() {
  try { return JSON.parse(localStorage.getItem('usuario') || 'null')?.id || null; } catch (_) { return null; }
}

function foto() {
  return {
    ...st,
    pares: new Map([...st.pares].map(([k, v]) => [k, { ...v }])),
    ativas: new Map(st.ativas),
    local: { ...st.local },
    brindes: [...st.brindes],
    reacoes: [...st.reacoes],
    prefs: { ...st.prefs },
  };
}
function notificar() {
  const copia = foto();
  for (const fn of [...assinantes]) { try { fn(copia); } catch (_) { /* ok */ } }
}
export function assinar(fn) {
  assinantes.add(fn);
  fn(foto());
  return () => assinantes.delete(fn);
}
export function estadoAtual() { return st; }

function brinde(texto) {
  const id = ++seq;
  st.brindes = [...st.brindes, { id, texto }].slice(-3);
  notificar();
  setTimeout(() => { st.brindes = st.brindes.filter((b) => b.id !== id); notificar(); }, 3500);
}
function avisar(texto, segundos) {
  st.aviso = texto || '';
  notificar();
  if (texto && segundos) {
    setTimeout(() => { if (st.aviso === texto) { st.aviso = ''; notificar(); } }, segundos * 1000);
  }
}
function soltarReacao(quem, emoji) {
  const id = ++seq;
  st.reacoes = [...st.reacoes, { id, quem, emoji, x: 20 + Math.round(Math.random() * 60) }].slice(-12);
  notificar();
  setTimeout(() => { st.reacoes = st.reacoes.filter((r) => r.id !== id); notificar(); }, 2600);
}

// ── toque ───────────────────────────────────────────────────────────────────
let toqueTimer = null;
let audioCtx = null;
function ctxAudio() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx || audioCtx.state === 'closed') audioCtx = new Ctx();
  if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
  return audioCtx;
}
function _bip(freqs, dur) {
  try {
    const ctx = ctxAudio();
    if (!ctx) return;
    let t = ctx.currentTime;
    for (const f of freqs) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(f, t);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.11, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(t); o.stop(t + dur + 0.02);
      t += dur + 0.06;
    }
  } catch (_) { /* sem som */ }
}
function tocarToque(tipo) {
  pararToque();
  const um = () => (tipo === 'recebendo' ? _bip([660, 880, 660, 880], 0.18) : _bip([440, 480], 0.5));
  um();
  toqueTimer = setInterval(um, tipo === 'recebendo' ? 2200 : 3000);
}
function pararToque() { if (toqueTimer) { clearInterval(toqueTimer); toqueTimer = null; } }

// ── quem está falando ──────────────────────────────────────────────────────
const medidores = new Map(); // 'eu' | uid → { an, buf, fonte }
let medidorTimer = null;
function ouvirVolume(id, stream) {
  try {
    const ctx = ctxAudio();
    if (!ctx || !stream || !stream.getAudioTracks().length) return;
    const antigo = medidores.get(id);
    if (antigo) { try { antigo.fonte.disconnect(); } catch (_) { /* ok */ } }
    const fonte = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    fonte.connect(an);
    medidores.set(id, { an, fonte, buf: new Uint8Array(an.fftSize) });
  } catch (_) { /* sem Web Audio */ }
}
function _rms(m) {
  m.an.getByteTimeDomainData(m.buf);
  let soma = 0;
  for (const x of m.buf) soma += (x - 128) ** 2;
  return Math.sqrt(soma / m.buf.length);
}
/** Nível do meu microfone (0–100), pro medidor do quadro de configurações. */
export function nivelMic() {
  const m = medidores.get('eu');
  if (!m || !st.local.mic) return 0;
  return Math.min(100, _rms(m) * 4);
}
function medir() {
  let mudou = false;
  for (const [id, m] of medidores) {
    const falando = _rms(m) > 6 && (id === 'eu' ? st.local.mic : st.pares.get(id)?.mic !== false);
    if (id === 'eu') {
      if (st.local.falando !== falando) { st.local.falando = falando; mudou = true; }
    } else {
      const p = st.pares.get(id);
      if (p && p.falando !== falando) { p.falando = falando; mudou = true; }
    }
  }
  if (mudou) notificar();
}

// ── mídia local ─────────────────────────────────────────────────────────────
function faixa(tipo) {
  if (tipo === 'audio') return micTrack;
  if (tipo === 'video') return vivo(camTrack) ? camTrack : null;
  return vivo(telaTrack) ? telaTrack : null;
}

async function _prepararLocal(querVideo) {
  const md = navigator.mediaDevices;
  micTrack = null; camTrack = null;
  if (!md || !md.getUserMedia) {
    st.aviso = 'Este navegador não deixa usar câmera e microfone aqui.';
  } else {
    const audio = { echoCancellation: true, noiseSuppression: true, ...(st.prefs.mic ? { deviceId: { ideal: st.prefs.mic } } : {}) };
    const video = querVideo ? { width: { ideal: 1280 }, height: { ideal: 720 }, ...(st.prefs.cam ? { deviceId: { ideal: st.prefs.cam } } : {}) } : false;
    try {
      const s = await md.getUserMedia({ audio, video });
      micTrack = s.getAudioTracks()[0] || null;
      camTrack = s.getVideoTracks()[0] || null;
    } catch (e) {
      try {
        micTrack = (await md.getUserMedia({ audio })).getAudioTracks()[0] || null;
        if (querVideo) st.aviso = 'Câmera indisponível ou bloqueada: você entrou com áudio. Dá para ligar a câmera depois.';
      } catch (_) {
        st.aviso = e && e.name === 'NotAllowedError'
          ? 'O navegador bloqueou câmera e microfone. Libere no cadeado ao lado do endereço.'
          : 'Sem câmera e microfone: você vê e ouve os outros, mas não aparece. Libere o acesso no cadeado do navegador.';
      }
    }
  }
  st.local = {
    mic: !!micTrack, cam: vivo(camTrack), tela: false, mao: false, falando: false, telaAba: false,
    camStream: vivo(camTrack) ? new MediaStream([camTrack]) : null,
    telaStream: null,
  };
  if (micTrack) ouvirVolume('eu', new MediaStream([micTrack]));
}

function _soltarLocal() {
  for (const t of [micTrack, camTrack, telaTrack]) { try { t && t.stop(); } catch (_) { /* ok */ } }
  micTrack = null; camTrack = null; telaTrack = null;
  st.local = { mic: false, cam: false, tela: false, mao: false, falando: false, camStream: null, telaStream: null, telaAba: false };
}

// ── conexões ────────────────────────────────────────────────────────────────
function _sinal(para, tipo, dados) {
  if (!st.canalId) return;
  api.post(`/equipe-chat/canais/${st.canalId}/chamada/sinal`, { para, tipo, dados }).catch(() => { /* a outra ponta já saiu */ });
}
function _meuEstado() { return { mic: !!st.local.mic, cam: !!st.local.cam, tela: !!st.local.tela, mao: !!st.local.mao }; }
function _difundirEstado() { for (const uid of st.pares.keys()) _sinal(uid, 'midia', _meuEstado()); }

function _criarPar(uid, nome, ofertante) {
  const pc = new RTCPeerConnection({ iceServers });
  const par = {
    nome: nome || '', pc, remoto: new MediaStream(), telaStream: null,
    mic: true, cam: false, tela: false, mao: false, falando: false,
    conexao: 'conectando', filaIce: [], trans: {}, telaTrack: null,
  };
  if (ofertante) {
    for (const tipo of ORDEM) {
      par.trans[tipo] = pc.addTransceiver(faixa(tipo) || (tipo === 'audio' ? 'audio' : 'video'), { direction: 'sendrecv', streams: [new MediaStream()] });
    }
  }
  pc.ontrack = (e) => {
    const idx = pc.getTransceivers().indexOf(e.transceiver);
    const tipo = ORDEM[idx] || e.track.kind;
    if (tipo === 'tela') {
      par.telaTrack = e.track;
      par.telaStream = new MediaStream([e.track]);
    } else {
      for (const t of par.remoto.getTracks().filter((x) => x.kind === e.track.kind)) par.remoto.removeTrack(t);
      par.remoto.addTrack(e.track);
      // Stream novo pra que o <video>/<audio> perceba a troca de faixa.
      par.remoto = new MediaStream(par.remoto.getTracks());
      if (e.track.kind === 'audio') ouvirVolume(uid, par.remoto);
    }
    e.track.onunmute = () => notificar();
    e.track.onmute = () => notificar();
    notificar();
  };
  pc.onicecandidate = (e) => { if (e.candidate) _sinal(uid, 'ice', e.candidate.toJSON ? e.candidate.toJSON() : e.candidate); };
  pc.onconnectionstatechange = () => {
    const cs = pc.connectionState;
    par.conexao = cs === 'connected' ? 'ok' : cs === 'failed' ? 'falhou' : cs === 'disconnected' ? 'instavel' : 'conectando';
    if (cs === 'connected') _sinal(uid, 'midia', _meuEstado());
    if (cs === 'failed') {
      if (ofertante && pc.restartIce) {
        try { pc.restartIce(); pc.createOffer({ iceRestart: true }).then((o) => pc.setLocalDescription(o)).then(() => _sinal(uid, 'oferta', pc.localDescription)).catch(() => {}); } catch (_) { /* ok */ }
      }
      avisar(`Não foi possível ligar direto com ${par.nome || 'esta pessoa'} (a rede está bloqueando a conexão direta).`);
    }
    notificar();
  };
  st.pares.set(uid, par);
  return par;
}

async function _ofertarPara(uid, nome) {
  const par = _criarPar(uid, nome, true);
  const oferta = await par.pc.createOffer();
  await par.pc.setLocalDescription(oferta);
  _sinal(uid, 'oferta', par.pc.localDescription);
}

async function _aplicarIceFila(par) {
  const fila = par.filaIce.splice(0);
  for (const c of fila) { try { await par.pc.addIceCandidate(c); } catch (_) { /* candidato velho */ } }
}

async function _receberSinal(d) {
  const uid = Number(d.de);
  let par = st.pares.get(uid);
  if (d.tipo === 'oferta') {
    const novo = !par;
    const nome = par?.nome || st.nomes?.get(uid) || '';
    if (!par) par = _criarPar(uid, nome, false);
    await par.pc.setRemoteDescription(d.dados);
    // Quem responde passa a mandar nos canais que a oferta criou (áudio, câmera, tela).
    const txs = par.pc.getTransceivers();
    for (let i = 0; i < txs.length; i += 1) {
      const tipo = ORDEM[i] || txs[i].receiver.track.kind;
      par.trans[tipo] = txs[i];
      txs[i].direction = 'sendrecv';
      try { await txs[i].sender.replaceTrack(faixa(tipo)); } catch (_) { /* ok */ }
    }
    const resp = await par.pc.createAnswer();
    await par.pc.setLocalDescription(resp);
    _sinal(uid, 'resposta', par.pc.localDescription);
    await _aplicarIceFila(par);
    if (novo && nome) brinde(`${nome} entrou`);
  } else if (d.tipo === 'resposta') {
    if (!par) return;
    await par.pc.setRemoteDescription(d.dados);
    await _aplicarIceFila(par);
  } else if (d.tipo === 'ice') {
    if (!par) return;
    if (par.pc.remoteDescription) { try { await par.pc.addIceCandidate(d.dados); } catch (_) { /* ok */ } } else par.filaIce.push(d.dados);
  } else if (d.tipo === 'midia') {
    if (!par) return;
    const s = d.dados || {};
    if (s.mao && !par.mao) brinde(`✋ ${par.nome} levantou a mão`);
    if (s.tela && !par.tela) brinde(`🖥️ ${par.nome} está compartilhando a tela`);
    par.mic = s.mic !== false; par.cam = !!s.cam; par.tela = !!s.tela; par.mao = !!s.mao;
  } else if (d.tipo === 'reacao') {
    const emoji = d.dados?.emoji;
    if (par && REACOES.includes(emoji)) soltarReacao(uid, emoji);
  }
  notificar();
}

function _fecharPar(uid) {
  const par = st.pares.get(uid);
  if (!par) return;
  try { par.pc.close(); } catch (_) { /* ok */ }
  const m = medidores.get(uid);
  if (m) { try { m.fonte.disconnect(); } catch (_) { /* ok */ } medidores.delete(uid); }
  st.pares.delete(uid);
}

function _limpar(motivo) {
  pararToque();
  for (const uid of [...st.pares.keys()]) _fecharPar(uid);
  for (const m of medidores.values()) { try { m.fonte.disconnect(); } catch (_) { /* ok */ } }
  medidores.clear();
  if (medidorTimer) { clearInterval(medidorTimer); medidorTimer = null; }
  _soltarLocal();
  const eraCanal = st.canalId;
  st.fase = motivo ? 'encerrada' : 'livre';
  st.ultimoFim = motivo ? { canalId: eraCanal, motivo } : null;
  st.canalId = null; st.canalNome = ''; st.convite = null; st.inicio = null; st.nomes = null;
  st.brindes = []; st.reacoes = [];
  try { if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {}); } catch (_) { /* ok */ }
  notificar();
  if (motivo) setTimeout(() => { if (st.fase === 'encerrada') { st.fase = 'livre'; st.ultimoFim = null; st.aviso = ''; notificar(); } }, 2600);
}

// ── ações ───────────────────────────────────────────────────────────────────
function _entrouNaChamada(resp, canalId, canalNome) {
  if (Array.isArray(resp?.ice_servers) && resp.ice_servers.length) iceServers = resp.ice_servers;
  st.canalId = Number(canalId);
  st.canalNome = canalNome || st.canalNome || '';
  st.video = resp?.video !== false;
  st.inicio = resp?.inicio || new Date().toISOString();
  st.nomes = new Map((resp?.participantes || []).map((p) => [Number(p.id), p.nome]));
  if (resp?.chamada_id) st.ativas.set(st.canalId, resp);
  if (!medidorTimer) medidorTimer = setInterval(medir, 250);
}

/** Liga numa conversa (ou entra, se já tem chamada nela). */
export async function iniciar(canalId, canalNome, { video = true } = {}) {
  if (st.fase !== 'livre' && st.fase !== 'encerrada') return;
  st.aviso = '';
  st.fase = 'chamando'; st.canalId = Number(canalId); st.canalNome = canalNome || ''; st.video = video;
  notificar();
  await _prepararLocal(video);
  try {
    const { data } = await api.post(`/equipe-chat/canais/${canalId}/chamada`, { video });
    _entrouNaChamada(data, canalId, canalNome);
    const ligar = data.ligar_para || [];
    if (ligar.length) {
      st.fase = 'em_chamada';
      for (const uid of ligar) await _ofertarPara(Number(uid), st.nomes.get(Number(uid)));
    } else {
      tocarToque('chamando');
    }
    notificar();
  } catch (e) {
    st.aviso = e.response?.data?.erro || 'Não consegui iniciar a chamada.';
    _limpar('erro');
  }
}

/** Atende o convite (ou entra numa chamada em andamento). */
export async function entrar(canalId, canalNome, { video = true } = {}) {
  if (st.fase === 'em_chamada' || st.fase === 'chamando') return;
  pararToque();
  st.aviso = '';
  st.fase = 'em_chamada'; st.canalId = Number(canalId); st.canalNome = canalNome || st.convite?.canal_nome || '';
  st.convite = null;
  notificar();
  await _prepararLocal(video);
  try {
    const { data } = await api.post(`/equipe-chat/canais/${canalId}/chamada/entrar`);
    _entrouNaChamada(data, canalId, st.canalNome);
    for (const uid of data.ligar_para || []) await _ofertarPara(Number(uid), st.nomes.get(Number(uid)));
    notificar();
  } catch (e) {
    st.aviso = e.response?.data?.erro || 'Não consegui entrar na chamada.';
    _limpar('erro');
  }
}

export function recusar() {
  const c = st.convite;
  pararToque();
  if (c) api.post(`/equipe-chat/canais/${c.canal_id}/chamada/recusar`).catch(() => {});
  st.convite = null; st.fase = 'livre'; st.canalId = null;
  notificar();
}

export function sair() {
  const id = st.canalId;
  if (id) api.post(`/equipe-chat/canais/${id}/chamada/sair`).catch(() => {});
  _limpar(null);
}

/** Troca a faixa de um canal em todas as conexões, sem recombinar. */
async function _trocarFaixa(tipo) {
  const t = faixa(tipo);
  for (const par of st.pares.values()) {
    const tr = par.trans[tipo];
    if (tr) { try { await tr.sender.replaceTrack(t); } catch (_) { /* ok */ } }
  }
  st.local.camStream = vivo(camTrack) ? new MediaStream([camTrack]) : null;
  st.local.telaStream = vivo(telaTrack) ? new MediaStream([telaTrack]) : null;
  _difundirEstado();
  notificar();
}

export async function alternarMic() {
  if (!micTrack) {
    try {
      const audio = { echoCancellation: true, noiseSuppression: true, ...(st.prefs.mic ? { deviceId: { ideal: st.prefs.mic } } : {}) };
      micTrack = (await navigator.mediaDevices.getUserMedia({ audio })).getAudioTracks()[0];
      st.local.mic = true;
      ouvirVolume('eu', new MediaStream([micTrack]));
      await _trocarFaixa('audio');
    } catch (_) { avisar('Microfone bloqueado: libere no cadeado do navegador.', 6); }
    return;
  }
  micTrack.enabled = !micTrack.enabled;
  st.local.mic = micTrack.enabled;
  _difundirEstado();
  notificar();
}

export async function alternarCam() {
  if (vivo(camTrack)) {
    // Desligar solta a câmera de verdade (a luz apaga); ligar pede de novo.
    try { camTrack.stop(); } catch (_) { /* ok */ }
    camTrack = null; st.local.cam = false;
    await _trocarFaixa('video');
    return;
  }
  try {
    const video = { width: { ideal: 1280 }, height: { ideal: 720 }, ...(st.prefs.cam ? { deviceId: { ideal: st.prefs.cam } } : {}) };
    camTrack = (await navigator.mediaDevices.getUserMedia({ video })).getVideoTracks()[0];
    st.local.cam = true;
    if (st.aviso.startsWith('Câmera')) st.aviso = '';
    await _trocarFaixa('video');
  } catch (_) {
    avisar('Câmera bloqueada ou em uso por outro programa.', 6);
  }
}

export async function alternarTela() {
  if (st.local.tela) {
    if (telaTrack) { try { telaTrack.stop(); } catch (_) { /* ok */ } }
    telaTrack = null; st.local.tela = false; st.local.telaAba = false;
    await _trocarFaixa('tela');
    return;
  }
  if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
    avisar('Este navegador não compartilha a tela.', 6); return;
  }
  try {
    // cursor: 'always' pede ao navegador pra desenhar a seta do mouse na imagem compartilhada.
    const s = await navigator.mediaDevices.getDisplayMedia({
      video: { cursor: 'always', frameRate: { ideal: 15, max: 30 }, width: { max: 1920 }, height: { max: 1080 } },
      audio: false, selfBrowserSurface: 'exclude', surfaceSwitching: 'include',
    });
    telaTrack = s.getVideoTracks()[0];
    try { telaTrack.contentHint = 'detail'; } catch (_) { /* prioriza nitidez do texto */ }
    telaTrack.onended = () => { if (st.local.tela) alternarTela(); };
    st.local.tela = true;
    const sup = telaTrack.getSettings ? telaTrack.getSettings().displaySurface : '';
    st.local.telaAba = sup === 'browser';
    await _trocarFaixa('tela');
    if (st.local.telaAba) {
      avisar('Você está compartilhando uma aba: nesse modo o navegador não mostra a seta do mouse. Para aparecer, compartilhe a "Janela" ou a "Tela inteira".', 10);
    } else {
      brinde(st.local.cam ? '🖥️ Compartilhando a tela e a câmera' : '🖥️ Compartilhando a tela');
    }
  } catch (_) { /* cancelou a escolha da tela */ }
}

export function alternarMao() {
  st.local.mao = !st.local.mao;
  _difundirEstado();
  notificar();
}

export function reagir(emoji) {
  if (!REACOES.includes(emoji)) return;
  soltarReacao('eu', emoji);
  for (const uid of st.pares.keys()) _sinal(uid, 'reacao', { emoji });
}

/** Microfones, câmeras e alto-falantes do computador. */
export async function dispositivos() {
  try { return await navigator.mediaDevices.enumerateDevices(); } catch (_) { return []; }
}
export function dispositivoAtual(tipo) {
  if (tipo === 'audioinput') return micTrack?.getSettings?.().deviceId || st.prefs.mic || '';
  if (tipo === 'videoinput') return camTrack?.getSettings?.().deviceId || st.prefs.cam || '';
  return st.prefs.som || 'default';
}

export async function trocarMicrofone(id) {
  try {
    const novo = (await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: id }, echoCancellation: true, noiseSuppression: true } })).getAudioTracks()[0];
    novo.enabled = micTrack ? micTrack.enabled : true;
    try { micTrack && micTrack.stop(); } catch (_) { /* ok */ }
    micTrack = novo;
    st.local.mic = novo.enabled;
    ouvirVolume('eu', new MediaStream([novo]));
    st.prefs.mic = id; salvarPrefs();
    await _trocarFaixa('audio');
  } catch (_) { avisar('Não deu para usar esse microfone.', 5); }
}

export async function trocarCamera(id) {
  st.prefs.cam = id; salvarPrefs();
  if (!vivo(camTrack)) { notificar(); return; } // vale na próxima vez que ligar a câmera
  try {
    const novo = (await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: id }, width: { ideal: 1280 }, height: { ideal: 720 } } })).getVideoTracks()[0];
    try { camTrack.stop(); } catch (_) { /* ok */ }
    camTrack = novo;
    st.local.cam = true;
    await _trocarFaixa('video');
  } catch (_) { avisar('Não deu para usar essa câmera.', 5); }
}

export function trocarAltoFalante(id) {
  st.prefs.som = id; salvarPrefs();
  notificar(); // os <audio> da tela aplicam o setSinkId
}

// ── eventos do servidor ─────────────────────────────────────────────────────
function aoEvento(d) {
  if (!d || !d.acao) return;
  const canal = Number(d.canal_id);
  if (d.acao === 'estado') {
    st.ativas.set(canal, d);
    if (st.canalId === canal && st.nomes) for (const p of d.participantes || []) st.nomes.set(Number(p.id), p.nome);
    notificar();
    return;
  }
  if (d.acao === 'fim') {
    st.ativas.delete(canal);
    if (st.convite && Number(st.convite.canal_id) === canal) { pararToque(); st.convite = null; st.fase = 'livre'; notificar(); }
    if (st.canalId === canal && (st.fase === 'em_chamada' || st.fase === 'chamando')) {
      st.aviso = d.motivo === 'sem_resposta' ? 'Ninguém atendeu.' : d.motivo === 'recusada' ? 'Chamada recusada.' : '';
      _limpar(d.motivo || 'fim');
    } else notificar();
    return;
  }
  if (d.acao === 'convite') {
    st.ativas.set(canal, d);
    if (st.fase !== 'livre' && st.fase !== 'encerrada') { notificar(); return; } // já está em outra: só aparece "entrar" no chat
    st.fase = 'tocando';
    st.convite = { ...d };
    tocarToque('recebendo');
    if (document.visibilityState !== 'visible' && typeof window.Notification !== 'undefined' && window.Notification.permission === 'granted') {
      try {
        const n = new window.Notification(`${d.video ? '🎥' : '📞'} ${d.de?.nome || 'Chamada'}`, {
          body: d.canal_tipo === 'direto' ? 'está te ligando' : `está ligando em ${d.canal_nome}`, tag: `chamada-${canal}`, icon: '/logo192.png', requireInteraction: true,
        });
        n.onclick = () => { window.focus(); n.close(); };
      } catch (_) { /* ok */ }
    }
    notificar();
    return;
  }
  if (st.canalId !== canal) return;
  if (d.acao === 'entrou') {
    if (st.nomes) st.nomes.set(Number(d.usuario?.id), d.usuario?.nome || '');
    if (st.fase === 'chamando') { pararToque(); st.fase = 'em_chamada'; }
    notificar();
  } else if (d.acao === 'saiu') {
    const nome = st.pares.get(Number(d.usuario_id))?.nome;
    _fecharPar(Number(d.usuario_id));
    if (nome) brinde(`${nome} saiu`);
    notificar();
  } else if (d.acao === 'recusou') {
    brinde(`${d.usuario?.nome || 'Alguém'} recusou`);
  } else if (d.acao === 'sinal') {
    if (Number(d.de) === meuId()) return;
    _receberSinal(d).catch(() => { /* sinal fora de ordem: a conexão se refaz */ });
  }
}

let ligado = false;
/** Chamado uma vez pelo aviso global do chat (mora no layout do cockpit). */
export function ligarMotor() {
  if (ligado) return () => {};
  ligado = true;
  const soltar = usar();
  const carregar = () => api.get('/equipe-chat/chamadas').then(({ data }) => {
    st.ativas = new Map((data?.chamadas || []).map((c) => [Number(c.canal_id), c]));
    if (Array.isArray(data?.ice_servers) && data.ice_servers.length) iceServers = data.ice_servers;
    notificar();
  }).catch(() => {});
  carregar();
  const offs = [ouvir('chamada', aoEvento), ouvir('pronto', carregar)];
  // Fechou a aba no meio da chamada: avisa na hora (se não der, o servidor tira em ~20s quando a conexão cai).
  const aoFechar = () => {
    if (!st.canalId || (st.fase !== 'em_chamada' && st.fase !== 'chamando')) return;
    try {
      fetch(`${API_BASE}/equipe-chat/canais/${st.canalId}/chamada/sair`, {
        method: 'POST', keepalive: true, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      }).catch(() => {});
    } catch (_) { /* ok */ }
  };
  window.addEventListener('pagehide', aoFechar);
  return () => { ligado = false; offs.forEach((f) => f()); window.removeEventListener('pagehide', aoFechar); soltar(); };
}
